import HomeIcon from '../../assets/icons/home-icon.svg?react';
import HomeActiveIcon from '../../assets/icons/home-active-icon.svg?react';
import ReportIcon from '../../assets/icons/report-outline-icon.svg?react';
import ReportActiveIcon from '../../assets/icons/report-icon.svg?react';
import ChartIcon from '../../assets/icons/chart-icon.svg?react';
import ChartActiveIcon from '../../assets/icons/chart-active-icon.svg?react';
import ManagerCardIcon from '../../assets/icons/manager-card-icon.svg?react';
import ManagerCardActiveIcon from '../../assets/icons/manager-card-active-icon.svg?react';
import HammerIcon from '../../assets/icons/hammer-icon.svg?react';
import HammerActiveIcon from '../../assets/icons/hammer-active-icon.svg?react';
import HelpIcon from '../../assets/icons/help-icon.svg?react';
import HelpActiveIcon from '../../assets/icons/help-active-icon.svg?react';
import AccountIcon from '../../assets/icons/account-icon.svg?react';
import UserIcon from '../../assets/icons/user-icon.svg?react';
import InjuryIcon from '../../assets/icons/injuries-icon.svg?react';
import DocumentIcon from '../../assets/icons/document-icon.svg?react';
import DocumentActiveIcon from '../../assets/icons/document-active-icon.svg?react';
import ReportFileOutlineIcon from '../../assets/icons/report-file-outline-icon.svg?react';
import ReportFileFilledIcon from '../../assets/icons/report-file-filled-icon.svg?react';
import InjuriesOutlineIcon from '../../assets/icons/injuries-outline-icon.svg?react';
import { LoginResponseData } from '@EHSNavigator/backend';
import { UserWithOrgContext } from '../../types/user';

export const createNavLinks = (userInfo: UserWithOrgContext | LoginResponseData | null) => {
  if (!userInfo) return [];

  const {
    user: {
      role: { permissions },
    },
  } = userInfo;

  const hasPermission = (path: string) => {
    return permissions?.some((node) => node?.name?.includes(path));
  };

  const shouldShowParent = (links: { link: string; visible?: boolean }[]) => {
    return links.some((link) => link.visible !== false);
  };

  const isAppOwner = Boolean(userInfo?.user?.isAppOwner);
  const hasOrgContext = Boolean((userInfo as UserWithOrgContext)?.orgContext);

  return [
    {
      title: 'Home',
      link: '/dashboard',
      icon: <HomeIcon className="svg-path-current-color-fill" />,
      activeIcon: <HomeActiveIcon className="svg-path-current-color-fill" />,
      visible: !hasOrgContext,
    },

    {
      title: 'Admin',
      icon: <UserIcon className="svg-path-current-color-fill" />,
      activeIcon: <UserIcon className="svg-path-current-color-fill" />,
      visible: isAppOwner && !hasOrgContext,
      links: [
        {
          title: 'Customers',
          link: '/admin/customers',
          visible: isAppOwner && !hasOrgContext,
        },
        {
          title: 'Onboard Organization',
          link: '/admin/onboard-organization',
          visible: isAppOwner && !hasOrgContext,
        },
        {
          title: 'Organization Requests',
          link: '/admin/organization-requests',
          visible: isAppOwner && !hasOrgContext,
        },
      ],
    },

    {
      title: 'Incidents',
      icon: <ReportIcon className="svg-path-current-color-fill" />,
      activeIcon: <ReportActiveIcon className="svg-path-current-color-fill" />,
      visible: !hasOrgContext,
      links: [
        {
          title: 'Report Incident',
          link: '/report-incident',
          visible: true,
        },
        {
          title: 'My Reported Incidents',
          link: '/my-reported-incidents',
          visible: true,
        },
      ],
    },
    {
      title: 'Injuries',
      link: '/injuries',
      icon: <InjuriesOutlineIcon className="svg-path-current-color-fill" />,
      activeIcon: <InjuryIcon className="svg-path-current-color-fill" />,
      visible: hasPermission('/injuries') && !hasOrgContext,
    },
    {
      title: 'Statistics',
      link: '/statistics',
      icon: <ChartIcon className="svg-path-current-color-fill" />,
      activeIcon: <ChartActiveIcon className="svg-path-current-color-fill" />,
      visible: hasPermission('/statistics') && !hasOrgContext,
    },
    {
      title: 'Manager Review',
      icon: <ManagerCardIcon className="svg-path-current-color-fill" />,
      activeIcon: (
        <ManagerCardActiveIcon className="svg-path-current-color-fill" />
      ),
      links: [
        {
          title: 'Incidents Review',
          link: '/incident-review',
          visible: hasPermission('/incident-review'),
        },
        {
          title: 'Assigned Corrective Actions',
          link: '/assigned-corrective-actions',
          visible: hasPermission('/assigned-corrective-actions'),
        },
      ],
      visible: !hasOrgContext && shouldShowParent([
        {
          link: '/incident-review',
          visible: hasPermission('/incident-review'),
        },
        {
          link: '/assigned-corrective-actions',
          visible: hasPermission('/assigned-corrective-actions'),
        },
      ]),
    },
    {
      title: 'Corrective Actions',
      icon: <HammerIcon className="svg-path-current-color-fill" />,
      activeIcon: <HammerActiveIcon className="svg-path-current-color-fill" />,
      visible: !hasOrgContext,
      links: [
        {
          title: 'Assigned to Me',
          link: '/assigned-to-me',
          visible: true,
        },
        {
          title: 'All Corrective Actions',
          link: '/all-corrective-actions',
          visible: hasPermission('/all-corrective-actions'),
        },
      ],
    },
    {
      title: 'User Management',
      icon: <UserIcon className="svg-path-current-color-fill" />,
      activeIcon: <UserIcon className="svg-path-current-color-fill" />,
      links: [
        {
          title: 'User Roles',
          link: '/user-roles',
          visible: hasOrgContext || hasPermission('/user-roles'),
        },
        {
          title: 'Employee Onboarding',
          link: '/employee-onboarding',
          visible: hasOrgContext || hasPermission('/employee-onboarding'),
        },
        {
          title: 'Bulk User Creation',
          link: '/bulk-user-creation',
          visible: hasOrgContext || hasPermission('/bulk-user-creation'),
        },
        {
          title: 'Task Review',
          link: '/task-review',
          visible: hasOrgContext || hasPermission('/task-review'),
        },
        {
          title: 'Departments',
          link: '/departments',
          visible: hasOrgContext || hasPermission('/departments'),
        },
      ],
      visible: hasOrgContext || shouldShowParent([
        { link: '/user-roles', visible: hasPermission('/user-roles') },
        {
          link: '/employee-onboarding',
          visible: hasPermission('/employee-onboarding'),
        },
        {
          link: '/bulk-user-creation',
          visible: hasPermission('/bulk-user-creation'),
        },
        {
          link: '/task-review',
          visible: hasPermission('/task-review'),
        },
        { link: '/departments', visible: hasPermission('/departments') },
      ]),
    },
    {
      title: 'OSHA Reporting',
      link: '/osha-reporting',
      icon: <ReportFileOutlineIcon className="svg-path-current-color-fill" />,
      activeIcon: (
        <ReportFileFilledIcon className="svg-path-current-color-fill" />
      ),
      visible: hasPermission('/osha-reporting') && !hasOrgContext,
    },
    {
      title: 'My Account',
      icon: <AccountIcon className="svg-path-current-color-stroke" />,
      activeIcon: <AccountIcon className="svg-path-current-color-stroke" />,
      visible: !hasOrgContext,
      links: [
        {
          title: 'General Settings',
          link: '/general-settings',
          visible: true,
        },
        {
          title: 'Custom Categories',
          link: '/custom-categories',
          visible: hasPermission('/custom-categories'),
        },
        {
          title: 'Custom Questions',
          link: '/custom-questions',
          visible: hasPermission('/custom-questions'),
        },
      ],
    },
    {
      title: 'Documents',
      link: '/documents',
      icon: <DocumentIcon className="svg-path-current-color-fill" />,
      activeIcon: (
        <DocumentActiveIcon className="svg-path-current-color-fill" />
      ),
      visible: !hasOrgContext,
    },
    {
      title: 'Help & Support',
      icon: <HelpIcon className="svg-path-current-color-fill" />,
      activeIcon: <HelpActiveIcon className="svg-path-current-color-fill" />,
      visible: !hasOrgContext,
      links: [
        {
          title: 'User Guides',
          link: '/user-guides',
          visible: true,
        },
        {
          title: 'FAQs',
          link: '/faqs',
          visible: true,
        },
        {
          title: 'Contact Support',
          link: '/contact-support',
          visible: true,
        },
      ],
    },
    // {
    //   title: 'Admin',
    //   icon: <UserIcon className="svg-path-current-color-fill" />,
    //   activeIcon: <UserIcon className="svg-path-current-color-fill" />,
    //   visible: isAppOwner && !hasOrgContext,
    //   links: [
    //     {
    //       title: 'Customers',
    //       link: '/admin/customers',
    //       visible: isAppOwner && !hasOrgContext,
    //     },
    //     {
    //       title: 'Onboard Organization',
    //       link: '/admin/onboard-organization',
    //       visible: isAppOwner && !hasOrgContext,
    //     },
    //     {
    //       title: 'Organization Requests',
    //       link: '/admin/organization-requests',
    //       visible: isAppOwner && !hasOrgContext,
    //     },
    //   ],
    // },
  ];
};
